/**
 * "Save as asset" — promotion (CONTENT_MODEL_SPEC §3.5, SCENE_ROOT_SPEC §4).
 *
 * Promotion is the one gesture that moves bytes **across** the scene/asset
 * line. Everything structural about it belongs to the viewer
 * (`viewer.promoteSubtreeToAsset`): the single-root contract, the
 * document-only grouping of a multi-selection, the closure rule, and the
 * `destination: 'other'` fetch that brings a folder-loaded scene's files back
 * as bytes because the *new* asset folder holds none of them yet.
 *
 * What is left for the editor, and lives here, is *which* write a promotion
 * is — and it is the ordinary one: a new library asset, created as an empty
 * row and then filled in by the same delta publish an edit does
 * (`create-asset-folder.ts`). The document + repeated `Files` multipart this
 * module used to build is gone: the server mints storage names and validates
 * the document against them as uploaded (§2.1, §2.4), so a one-shot create
 * could only have worked when nothing new travelled at all.
 *
 * **Offenders are checked by the caller, not swallowed here.** A promotion
 * with offenders is one the server refuses (spec §2.5), so it must never
 * reach the wire — {@link uploadPromotedAsset} refuses it as a last line of
 * defence, but the editor is expected to have shown the author the offending
 * `files[]` entries and stopped before that.
 */

import { formatManifestOffenders } from 'trace-viewer/editor';
import type { ManifestOffender, PromotedAsset, SceneFolder } from 'trace-viewer/editor';
import { EntityType } from 'trace-viewer/cms';
import {
    publishNewAssetFolder,
    type AssetFolderCreateClient,
    type AssetFolderTarget,
} from './create-asset-folder';

/**
 * Where a promotion lands, and what makes the calls.
 *
 * Re-exported rather than redeclared: a promotion creates a library asset like
 * any other create, so it has no target type and no client of its own. The
 * names stay for the callers that already speak them.
 */
export type { AssetFolderTarget };
export type AssetCreateClient = AssetFolderCreateClient;

/** A promotion the closure rule refuses (spec §3.2) — reported, never uploaded. */
export class PromotionRefusedError extends Error {
    readonly offenders: ManifestOffender[];

    constructor(offenders: readonly ManifestOffender[]) {
        super(
            `This selection cannot become a library asset: ${offenders.length} reference(s) `
            + `the asset closure rule refuses (CONTENT_MODEL_SPEC §3.2).\n`
            + formatManifestOffenders(offenders),
        );
        this.name = 'PromotionRefusedError';
        this.offenders = [...offenders];
    }
}

/**
 * Create the library asset: an empty row, the files that travel with the
 * promotion, then the document.
 *
 * A `PromotedAsset` is a {@link SceneFolder} that has not been called one —
 * the same document and the same `uri → bytes` map, plus the offenders the
 * closure rule found. `existing` is empty and says so explicitly: the folder
 * this is landing in was created moments ago and holds nothing, so every file
 * the document names has to travel.
 *
 * `EntityType.Component` is what a promoted subtree *is* (SCENE_ROOT_SPEC §4):
 * a reusable piece of content with one structural root, not a whole scene and
 * not a bare model file.
 *
 * @returns the new asset's id — the identity a reference node is minted from
 *   (`{apiBase}/assets/{id}/file`, spec §3.4).
 */
export async function uploadPromotedAsset(
    client: AssetCreateClient,
    promoted: PromotedAsset,
    target: AssetFolderTarget,
    title: string,
    thumbnail?: File,
): Promise<string> {
    if (promoted.offenders.length > 0) throw new PromotionRefusedError(promoted.offenders);

    const folder: SceneFolder = {
        document: promoted.document,
        files: promoted.files,
        existing: new Set<string>(),
    };

    const { assetId } = await publishNewAssetFolder(client, folder, {
        target,
        title,
        entityType: EntityType.Component,
        ...(thumbnail ? { thumbnail } : {}),
    });
    return assetId;
}
